import { useState, useEffect, useCallback } from 'react';
import api from '@/api';

/**
 * useRepairCategories
 * Fetches the admin-managed list of repair event categories.
 *
 * Replaces the old hardcoded `CATS` arrays scattered across the app.
 * Caches per browser-session so back/forward navigation doesn't re-fetch.
 */
let _cache = null;
let _inflight = null;

export default function useRepairCategories({ includeInactive = false } = {}) {
  const [categories, setCategories] = useState(_cache || []);
  const [loading, setLoading] = useState(!_cache);
  const [error, setError] = useState(null);

  const load = useCallback(async (force = false) => {
    if (!force && _cache && !includeInactive) {
      setCategories(_cache);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      if (_inflight && !force && !includeInactive) {
        const data = await _inflight;
        setCategories(data);
        return;
      }
      const promise = api
        .get('/repair-event-categories', { params: includeInactive ? { include_inactive: true } : {} })
        .then((r) => r.data.items || []);
      if (!includeInactive) _inflight = promise;
      const items = await promise;
      if (!includeInactive) {
        _cache = items;
      }
      setCategories(items);
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
      if (!includeInactive) _inflight = null;
    }
  }, [includeInactive]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeInactive]);

  const invalidate = useCallback(() => {
    _cache = null;
    _inflight = null;
    load(true);
  }, [load]);

  // Convenience: array of just the names (preserving sort order)
  const names = categories.map((c) => c.name);

  return { categories, names, loading, error, reload: invalidate };
}
